/**
 * Settings Service
 *
 * Service for loading and processing application settings including RUM configuration
 */

import { getA3SSettings } from "@helpers/Settings";
import { ISettings } from "@defs/Settings";
import { RumAppConfig } from "./rumService";

/**
 * SettingsService - Manages application settings and RUM configuration
 *
 * Handles loading of A3S frontend settings and generates RUM configuration
 */
export const SettingsService = {
    /**
     * Load application settings from cookies or development endpoint
     */
    loadSettings: async (): Promise<ISettings> => {
        try {
            // eslint-disable-next-line no-undef
            const settings = __DEV__ ? await fetch("/a3s-settings.json").then((res) => res.json()) : getA3SSettings();

            console.log("Loaded application settings for stage:", settings?.stage);
            return settings;
        } catch (error) {
            console.error("Failed to load application settings:", error);
            throw error;
        }
    },

    /**
     * Generate RUM configuration from application settings
     */
    getRumConfig: (settings: ISettings): RumAppConfig => {
        return {
            applicationId: settings?.rum?.applicationId || "",
            applicationVersion: "1.0.0",
            applicationRegion: settings?.rum?.region || "us-west-2",
            identityPoolId: settings?.cognito?.identityPoolId || "",
            config: {
                endpoint: settings?.rum?.endpoint?.startsWith("http")
                    ? settings.rum.endpoint
                    : `${window.location.origin}${settings?.rum?.endpoint || "/rum-data"}`,
                sessionSampleRate: settings?.rum?.sessionSampleRate || 1,
                telemetries: settings?.rum?.telemetries || ["errors", "performance", "http"],
                allowCookies: settings?.rum?.allowCookies ?? true,
                enableXRay: settings?.rum?.enableXRay ?? true,
                signing: settings?.rum?.signing ?? false,
                sessionEventLimit: settings?.rum?.sessionEventLimit || 2000,
            },
        };
    },

    /**
     * Get stage from settings
     */
    getStage: (settings: ISettings): string => {
        return settings?.stage || "alpha";
    },
};
